/**
 * React Query Hooks for Agents
 *
 * Query hooks for agent configuration management
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { agentApi } from '../api';
import type { AgentListItem, AgentConfigResponse } from '@/types';

export const QUERY_KEYS = {
  agents: ['agents'] as const,
  agentsList: () => [...QUERY_KEYS.agents, 'list'] as const,
  agentConfig: (agentType: string) => [...QUERY_KEYS.agents, agentType] as const,
  agentVersions: (agentType: string) => [...QUERY_KEYS.agents, agentType, 'versions'] as const,
  availableAgents: () => [...QUERY_KEYS.agents, 'available'] as const,
};

/**
 * Get list of all agent configurations
 */
export function useAgentsListQuery(includeInactive = false): UseQueryResult<AgentListItem[], Error> {
  return useQuery({
    queryKey: [...QUERY_KEYS.agentsList(), includeInactive],
    queryFn: async () => {
      const response = await agentApi.list(includeInactive);
      return response.agents.map(agent => ({
        id: agent.agent_type,
        agent_type: agent.agent_type,
        name: agent.configuration.name,
        description: agent.configuration.description,
        is_active: agent.is_active,
        version: agent.version,
        updated_at: agent.updated_at,
      }));
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Get specific agent configuration
 */
export function useAgentConfigQuery(
  agentType: string,
  enabled = true
): UseQueryResult<AgentConfigResponse, Error> {
  return useQuery({
    queryKey: QUERY_KEYS.agentConfig(agentType),
    queryFn: async () => {
      const config = await agentApi.get(agentType);
      // Wrap in AgentConfigResponse format
      return {
        agent_type: agentType,
        version: 1,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        configuration: config,
      };
    },
    enabled: enabled && !!agentType,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Get agent configuration versions
 */
export function useAgentVersionsQuery(
  agentType: string,
  enabled = true
): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: QUERY_KEYS.agentVersions(agentType),
    queryFn: () => agentApi.getVersions(agentType),
    enabled: enabled && !!agentType,
    staleTime: 1000 * 60, // 1 minute
  });
}
